// src/pages/ShippingForm.js - CLEANED AND PROFESSIONAL
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const ShippingForm = () => {
  const { currentUser } = useAuth();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const navigate = useNavigate();

  const watchPaymentMethod = watch("paymentMethod", "");
  const watchAgreedPrice = watch("agreedPrice", 0);

  // Use environment variable for API URL with fallback
  const API_URL = process.env.REACT_APP_API_URL || 'https://us-central1-trucking-web.cloudfunctions.net';

  // Calculate fees and total
  const calculateFees = (basePrice) => {
    const price = Number(basePrice) || 0;
    if (price <= 0) return { basePrice: 0, fee: 0, total: 0 };
    
    if (watchPaymentMethod === 'Stripe' || watchPaymentMethod === 'Venmo') {
      const fee = price * 0.03; // 3% fee
      const total = price + fee;
      return {
        basePrice: price,
        fee: parseFloat(fee.toFixed(2)),
        total: parseFloat(total.toFixed(2))
      };
    }
    
    return {
      basePrice: price,
      fee: 0,
      total: price
    };
  };

  const fees = calculateFees(watchAgreedPrice);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setMessage('Please upload a JPEG, PNG, or PDF file');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('File size must be less than 5MB');
        return;
      }
      
      setReceiptFile(file);
      setMessage('');
    }
  };

  const handleAgreementChange = (e) => {
    setAgreementAccepted(e.target.checked);
    setAgreementError(false); // Clear error when user checks the box
  };

  const uploadReceipt = async (file, shipmentId) => {
    try {
      // Create a unique filename to avoid conflicts
      const fileExtension = file.name.split('.').pop();
      const fileName = `receipt_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `receipts/${shipmentId}/${fileName}`);
      
      // Add metadata for security rules
      const metadata = {
        customMetadata: {
          customerUserId: currentUser.uid,
          customerEmail: currentUser.email || '',
          uploadedAt: new Date().toISOString(),
          originalFileName: file.name,
          fileType: file.type,
          fileSize: file.size.toString(),
          shipmentId: shipmentId
        }
      };
      
      console.log('Uploading receipt to:', `receipts/${shipmentId}/${fileName}`);
      console.log('With metadata:', metadata);
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      console.log('Receipt uploaded successfully');
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Receipt download URL generated');
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading receipt:', error);
      throw new Error(`Failed to upload receipt: ${error.message}`);
    }
  };

  // Stripe payment handler - UPDATED FOR PRODUCTION
  const processStripePayment = async (shipmentData) => {
    try {
      console.log('Starting Stripe payment for shipment:', shipmentData.shipmentId);
      console.log('Using API URL:', API_URL);
      
      // Use the total amount including fees for Stripe payments
      const paymentAmount = shipmentData.feeAmount > 0 ? shipmentData.totalAmount : shipmentData.agreedPrice;
      
      // Ensure we have all required fields with defaults
      const requestData = {
        shipmentData: {
          ...shipmentData,
          agreedPrice: Number(paymentAmount) || 10.00,
          itemDescription: shipmentData.itemDescription || 'Shipping Service',
          pickupAddress: shipmentData.pickupAddress || 'Not specified',
          dropoffAddress: shipmentData.dropoffAddress || 'Not specified',
          invoiceNumber: shipmentData.invoiceNumber || 'N/A'
        },
        userId: currentUser.uid,
        userEmail: currentUser.email
      };

      console.log('Sending request to Stripe:', requestData);

      const response = await fetch(`${API_URL}/createCheckoutSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      console.log('Stripe response status:', response.status, response.statusText);
      
      // Check if response is OK first
      if (!response.ok) {
        let errorDetails;
        try {
          errorDetails = await response.json();
        } catch (e) {
          errorDetails = await response.text();
        }
        
        console.error('Server error details:', {
          status: response.status,
          statusText: response.statusText,
          body: errorDetails
        });
        
        throw new Error(`Payment server error (${response.status}): ${typeof errorDetails === 'object' ? errorDetails.error : errorDetails}`);
      }
      
      // Parse JSON response
      const result = await response.json();
      console.log('Stripe response data:', result);
      
      if (result.url) {
        console.log('Redirecting to Stripe checkout...');
        window.location.href = result.url;
      } else if (result.sessionId) {
        // Fallback: construct URL from sessionId
        console.log('Constructing Stripe URL from sessionId...');
        window.location.href = `https://checkout.stripe.com/c/pay/${result.sessionId}`;
      } else {
        console.error('No URL or sessionId in response:', result);
        throw new Error('No checkout URL received from server. Please contact support.');
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      
      // Enhanced error handling for production
      let userFriendlyMessage = error.message;
      if (error.message.includes('Failed to fetch')) {
        userFriendlyMessage = 'Network error: Unable to connect to payment service. Please check your internet connection and try again.';
      } else if (error.message.includes('404')) {
        userFriendlyMessage = 'Payment service temporarily unavailable. Please try again in a few moments.';
      } else if (error.message.includes('500')) {
        userFriendlyMessage = 'Payment service error. Our team has been notified. Please try again shortly.';
      }
      
      throw new Error(userFriendlyMessage);
    }
  };

  const onSubmit = async (data) => {
    if (!currentUser) {
      setMessage('Please log in to submit a shipping request.');
      return;
    }

    // Check if agreement is accepted - show error if not
    if (!agreementAccepted) {
      setAgreementError(true);
      setMessage('You must accept the payment agreement before submitting the form.');
      return;
    }

    // Enhanced payment validation
    if (watchPaymentMethod !== 'Stripe' && !receiptFile) {
      setMessage('Please upload your payment receipt screenshot for Zelle/Venmo payments');
      return;
    }

    setSubmitting(true);
    setMessage('');

    try {
      const shipmentId = `shipment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      let receiptUrl = '';
      if (receiptFile) {
        console.log('Starting receipt upload...');
        receiptUrl = await uploadReceipt(receiptFile, shipmentId);
        console.log('Receipt uploaded successfully, URL:', receiptUrl);
      }

      // ✅ FIXED: Ensure agreedPrice has a valid value
      const validatedAgreedPrice = Number(data.agreedPrice) || 0;

      // ✅ CRITICAL FIX: Only show in dashboard for non-Stripe payments with receipts
      // For Stripe payments, this will be updated to true by the webhook after successful payment
      const shouldShowInDashboard = watchPaymentMethod !== 'Stripe' && !!receiptUrl;

      // ✅ FIXED: Combine dimensions for email template
      const dimensions = `${data.length || 0}" L x ${data.width || 0}" W x ${data.height || 0}" H`;

      const shipmentData = {
        ...data,
        shipmentId: shipmentId,
        status: watchPaymentMethod === 'Stripe' ? 'awaiting_payment' : 'pending_verification',
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || data.name,
        receiptUrl: receiptUrl,
        invoiceNumber: data.invoiceNumber || '',
        agreedPrice: validatedAgreedPrice,
        // ✅ ADDED: Dimensions combined for email template
        dimensions: dimensions,
        // Store individual dimensions for future use
        length: data.length || 0,
        width: data.width || 0,
        height: data.height || 0,
        // Store fee information
        feeAmount: fees.fee,
        totalAmount: fees.total,
        paymentMethod: watchPaymentMethod,
        // ✅ FIXED: Control dashboard visibility
        shouldShowInDashboard: shouldShowInDashboard,
        // Initialize signatures as empty
        signatures: {
          driver: { signature: '', date: '' },
          customer: { signature: '', date: '' }
        },
        // Initialize BOL fields as empty
        bolUrl: '',
        customerCanViewBOL: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      console.log('Saving shipment data to Firestore:', shipmentData);
      console.log('Dashboard visibility:', shouldShowInDashboard ? 'VISIBLE' : 'HIDDEN');

      // Save shipment data to Firestore
      await setDoc(doc(db, 'shipments', shipmentId), shipmentData);
      console.log('Shipment saved to Firestore successfully');
      
      if (watchPaymentMethod === 'Stripe') {
        // Process Stripe payment - shipment is created but not shown in dashboard yet
        setMessage('Redirecting to secure payment...');
        await processStripePayment(shipmentData);
        // Note: The redirect will happen in processStripePayment
        // The webhook will update shouldShowInDashboard to true when payment is successful
      } else {
        // For Zelle/Venmo payments, show appropriate message
        if (receiptUrl) {
          setMessage('Thank you for submitting your request! Payment verification pending. Redirecting to dashboard...');
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setMessage('Shipment submitted! Please complete payment and upload receipt to appear in dashboard.');
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        }
      }
      
    } catch (error) {
      console.error("Error in form submission: ", error);
      
      // More user-friendly error messages
      let userFriendlyMessage = error.message;
      if (error.message.includes('storage/unauthorized')) {
        userFriendlyMessage = 'Storage access denied. Please check your permissions or contact support.';
      } else if (error.message.includes('permission') || error.message.includes('403')) {
        userFriendlyMessage = 'Permission denied. Please make sure you are logged in and try again.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        userFriendlyMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setMessage(`Error: ${userFriendlyMessage}. Please try again or contact support.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner"></div>
          <p className="mt-4 text-gray-600">Processing your shipment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-100 border border-red-400 text-red-700' 
              : message.includes('pending') || message.includes('complete')
              ? 'bg-yellow-100 border border-yellow-400 text-yellow-700'
              : 'bg-green-100 border border-green-400 text-green-700'
          }`}>
            {message}
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-blue-600 mb-2">
            Shipping Request Form
          </h1>
          <p className="text-gray-600 mb-6">
            Complete the form below to request a shipment. We'll get back to you shortly.
          </p>

          {/* Payment Agreement Notice */}
          <div className={`bg-blue-50 border rounded-lg p-4 mb-6 ${
            agreementError ? 'border-red-400 bg-red-50' : 'border-blue-200'
          }`}>
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              Important Payment Agreement
            </h3>
            <p className="text-blue-700 mb-3">
              <strong>Before filling out this shipping form:</strong>
            </p>
            <ul className="text-blue-700 list-disc list-inside space-y-1 mb-3">
              <li>Payment agreement must be made prior to filling out this form</li>
              <li>An invoice will be provided at the time of agreement</li>
              <li>The invoice number is required to complete this form</li>
              <li>Cash payments are not accepted</li>
              <li>Venmo and Stripe payments include a 3% processing fee</li>
              <li>Zelle payments have no additional fees</li>
            </ul>
            <p className="text-blue-700 mb-3">
              <strong>Need a quote or want to schedule a shipment?</strong>
            </p>
            <ul className="text-blue-700 list-disc list-inside space-y-1">
              <li>
                <a 
                  href="/quote" 
                  className="text-blue-600 underline hover:text-blue-800"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Fill out our Get a Quote form
                </a>
              </li>
              <li>
                Email us at:{" "}
                <a 
                  href="mailto:contact@flexlogisticsmanagement.com" 
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  contact@flexlogisticsmanagement.com
                </a>
              </li>
            </ul>
            <div className="flex items-start mt-4">
              <input
                type="checkbox"
                id="agreementAccepted"
                checked={agreementAccepted}
                onChange={handleAgreementChange}
                className={`mt-1 mr-2 ${agreementError ? 'border-red-500' : ''}`}
              />
              <label htmlFor="agreementAccepted" className={`font-medium ${
                agreementError ? 'text-red-700' : 'text-blue-800'
              }`}>
                I confirm that I have received the payment agreement and invoice, and understand the payment terms including fees.
              </label>
            </div>
            {agreementError && (
              <p className="mt-2 text-sm text-red-600 font-medium">
                You must accept the payment agreement to submit the form.
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Contact Information */}
            <div className="form-section">
              <h3 className="text-xl font-semibold mb-4">
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input 
                    {...register("name", { required: "Name is required" })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                    defaultValue={currentUser?.displayName || ''}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input 
                    {...register("email", { 
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="your@email.com"
                    defaultValue={currentUser?.email || ''}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input 
                    {...register("phone", { required: "Phone is required" })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Number *
                  </label>
                  <input 
                    {...register("invoiceNumber", { required: "Invoice number is required" })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.invoiceNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your invoice number"
                  />
                  {errors.invoiceNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.invoiceNumber.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <h3 className="text-xl font-semibold mb-4">
                Address Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pickup Address *
                  </label>
                  <textarea 
                    {...register("pickupAddress", { required: "Pickup address is required" })} 
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.pickupAddress ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter complete pickup address"
                  />
                  {errors.pickupAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.pickupAddress.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Drop-off Address *
                  </label>
                  <textarea 
                    {...register("dropoffAddress", { required: "Drop-off address is required" })} 
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.dropoffAddress ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter complete drop-off address"
                  />
                  {errors.dropoffAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.dropoffAddress.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Shipment Details */}
            <div className="form-section">
              <h3 className="text-xl font-semibold mb-4">
                Shipment Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Description *
                  </label>
                  <input 
                    {...register("itemDescription", { required: "Item description is required" })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.itemDescription ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe the item"
                  />
                  {errors.itemDescription && (
                    <p className="mt-1 text-sm text-red-600">{errors.itemDescription.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (lbs) *
                  </label>
                  <input 
                    type="number" 
                    {...register("weight", { required: "Weight is required", min: 0.1 })} 
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.weight ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.0"
                  />
                  {errors.weight && (
                    <p className="mt-1 text-sm text-red-600">{errors.weight.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <input 
                    type="number" 
                    {...register("quantity", { required: "Quantity is required", min: 1 })} 
                    defaultValue="1"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.quantity ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="1"
                  />
                  {errors.quantity && (
                    <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
                  )}
                </div>
              </div>

              {/* Dimensions Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Length (in) *
                  </label>
                  <input 
                    type="number" 
                    {...register("length", { required: "Length is required", min: 0.1 })} 
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.length ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.0"
                  />
                  {errors.length && (
                    <p className="mt-1 text-sm text-red-600">{errors.length.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Width (in) *
                  </label>
                  <input 
                    type="number" 
                    {...register("width", { required: "Width is required", min: 0.1 })} 
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.width ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.0"
                  />
                  {errors.width && (
                    <p className="mt-1 text-sm text-red-600">{errors.width.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Height (in) *
                  </label>
                  <input 
                    type="number" 
                    {...register("height", { required: "Height is required", min: 0.1 })} 
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.height ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.0"
                  />
                  {errors.height && (
                    <p className="mt-1 text-sm text-red-600">{errors.height.message}</p>
                  )}
                </div>
              </div>

              {/* Date Range Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pickup Date Range *
                  </label>
                  <input 
                    type="text" 
                    {...register("pickupDateRange", { required: "Pickup date range is required" })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.pickupDateRange ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Dec 15-20, 2024"
                  />
                  {errors.pickupDateRange && (
                    <p className="mt-1 text-sm text-red-600">{errors.pickupDateRange.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Date Range *
                  </label>
                  <input 
                    type="text" 
                    {...register("deliveryDateRange", { required: "Delivery date range is required" })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.deliveryDateRange ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Dec 18-25, 2024"
                  />
                  {errors.deliveryDateRange && (
                    <p className="mt-1 text-sm text-red-600">{errors.deliveryDateRange.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agreed Price ($) *
                  </label>
                  <input 
                    type="number" 
                    step="0.01"
                    min="0.01"
                    {...register("agreedPrice", { 
                      required: "Agreed price is required",
                      min: { value: 0.01, message: "Price must be at least $0.01" }
                    })} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.agreedPrice ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  {errors.agreedPrice && (
                    <p className="mt-1 text-sm text-red-600">{errors.agreedPrice.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Instructions
                  </label>
                  <textarea 
                    {...register("specialInstructions")} 
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any special handling instructions"
                  />
                </div>
              </div>

              {/* Fee Display */}
              {(watchPaymentMethod === 'Stripe' || watchPaymentMethod === 'Venmo') && fees.total > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-2">Payment Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Base Price:</span>
                      <span>${fees.basePrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Processing Fee (3%):</span>
                      <span>${fees.fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Total Amount:</span>
                      <span>${fees.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Section */}
            <div className="form-section">
              <h3 className="text-xl font-semibold mb-4">
                Payment Information
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Payment Method *
                </label>
                <select 
                  {...register("paymentMethod", { required: "Please select a payment method" })} 
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.paymentMethod ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Payment Method</option>
                  <option value="Zelle">Zelle (No fees)</option>
                  <option value="Venmo">Venmo (3% fee)</option>
                  <option value="Stripe">Credit/Debit Card (3% fee)</option>
                </select>
                {errors.paymentMethod && (
                  <p className="mt-1 text-sm text-red-600">{errors.paymentMethod.message}</p>
                )}
              </div>

              {/* Receipt Upload for non-Stripe payments */}
              {watchPaymentMethod && watchPaymentMethod !== "Stripe" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upload Payment Receipt Screenshot *
                  </label>
                  <input 
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Please upload a screenshot of your payment confirmation (JPEG, PNG, or PDF, max 5MB)
                  </p>
                  <p className="text-sm text-yellow-600 mt-1">
                    Note: Your shipment will only appear in dashboard after receipt upload and admin verification.
                  </p>
                </div>
              )}

              {/* Payment Instructions */}
              {watchPaymentMethod && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">Payment Instructions</h4>
                  
                  {watchPaymentMethod === "Zelle" && (
                    <div>
                      <p className="text-green-700">Please refer to your invoice for Zelle payment details.</p>
                      <p className="text-green-700 text-sm mt-1">Include your invoice number in the payment memo</p>
                      <p className="text-yellow-700 text-sm mt-1 font-semibold">Upload receipt above to complete submission</p>
                    </div>
                  )}
                  
                  {watchPaymentMethod === "Venmo" && (
                    <div>
                      <p className="text-green-700">Please refer to your invoice for Venmo payment details.</p>
                      <p className="text-green-700 text-sm mt-1">Include your invoice number in the payment note</p>
                      <p className="text-yellow-700 text-sm mt-1 font-semibold">Upload receipt above to complete submission</p>
                      <p className="text-red-600 text-sm mt-1 font-semibold">Note: 3% processing fee applies</p>
                    </div>
                  )}
                  
                  {watchPaymentMethod === "Stripe" && (
                    <div>
                      <p className="text-green-700">You will be redirected to our secure payment page after form submission.</p>
                      <p className="text-yellow-700 text-sm mt-1 font-semibold">Shipment will only appear in dashboard after successful payment</p>
                      <p className="text-red-600 text-sm mt-1 font-semibold">Note: 3% processing fee applies</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button 
              type="submit" 
              disabled={submitting || !agreementAccepted}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {submitting ? 'Processing...' : 'Submit Shipping Request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ShippingForm;